import { useEffect, useState, useCallback } from 'react';
import { Typography, Box, CircularProgress, Alert, Divider, Stack, Chip } from '@mui/material';
import DateRangeSelector from '../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../theme/pageCardSx';
import FilterSelectorWithApp from '../../components/FilterSelectorWithApp';
import NaverDirectDetailStatsTable from '../../components/naver/table/NaverDirectDetailStatsTable';
import apiClient from '../../utils/axiosClient';
import dayjs, { Dayjs } from 'dayjs';
import { NaverDirectDetailStats, NaverProviderCampaign } from '../../types/naver.types';

const ALL_APP = '앱 전체';

export default function NaverDirectDetailStatsPage() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [data, setData] = useState<NaverDirectDetailStats[] | null>(null);
  const [providerCampaigns, setProviderCampaigns] = useState<NaverProviderCampaign[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [selectedApp, setSelectedApp] = useState<string | null>(ALL_APP);
  const [appOptions, setAppOptions] = useState<string[]>([ALL_APP]);
  const [selectedOs, setSelectedOs] = useState('all');

  const fetchAppList = useCallback(() => {
    apiClient
      .get('/api/v1/stat/pointhome/app/list')
      .then((res) => setAppOptions([ALL_APP, ...res.data]))
      .catch(() => {
        // 옵션 로드 실패 시에도 화면은 동작하도록 침묵 처리
      });
  }, []);

  useEffect(() => {
    fetchAppList();
  }, [fetchAppList]);

  useEffect(() => {
    apiClient.get('/api/v1/stat/naver/campaign/list').then((res) => {
      setProviderCampaigns(res.data);
    });
  }, []);

  const fetchData = useCallback(() => {
    if (!startDate || !endDate) return;
    setLoading(true);
    setErrorMessage('');

    const params: Record<string, string> = {
      start_date: startDate.format('YYYY-MM-DD'),
      end_date: endDate.format('YYYY-MM-DD'),
    };
    if (selectedApp && selectedApp !== ALL_APP) params.app_name = selectedApp;
    if (selectedOs !== 'all') params.os_type = selectedOs;

    apiClient
      .get('/api/v1/stat/naver/direct/daily', { params })
      .then((res) => setData(res.data))
      .catch(() => setErrorMessage('데이터 로드에 실패했습니다. 잠시 후 다시 시도해주세요.'))
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, selectedOs]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <Box sx={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.3s ease', minHeight: '100vh' }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: { xs: 1.5, md: 0 },
        }}
      >
        <Box sx={{ width: { xs: '100%', md: 'auto' } }}>
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            onSearch={fetchData}
          />
        </Box>

        <Box sx={{ ml: { xs: 0, md: 3 }, mt: { xs: 1.5, md: 0 }, width: { xs: '100%', md: 'auto' } }}>
          <FilterSelectorWithApp
            selectedApp={selectedApp}
            appOptions={appOptions}
            onAppChange={(v) => setSelectedApp(v ?? ALL_APP)}
            selectedOs={selectedOs}
            onOsChange={setSelectedOs}
            onSearch={fetchData}
          />
        </Box>
      </Box>

      <Box
        sx={PAGE_CARD_SX}
      >
        {/* 앱 선택 (Chip) - 항상 렌더링되어 스크롤 위치 유지 */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            mb: 1,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#e2e8f0', borderRadius: 3 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#cbd5e1' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 600 : 500,
                backgroundColor: selectedApp === name ? '#0044D2' : '#f8fafc',
                color: selectedApp === name ? '#fff' : '#475569',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e2e8f0'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f1f5f9',
                  borderColor: selectedApp === name ? '#0033a0' : '#cbd5e1',
                },
                height: 36,
                fontSize: '0.85rem',
                borderRadius: 2,
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Stack>

        <Divider variant="fullWidth" sx={{ borderColor: '#f1f5f9', mb: 2 }} />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <CircularProgress size={50} sx={{ color: '#0044D2' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              데이터 로딩 중...
            </Typography>
          </Box>
        ) : errorMessage ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={400}>
            <Alert severity="error">{errorMessage}</Alert>
          </Box>
        ) : data && startDate && endDate && (
          <NaverDirectDetailStatsTable
            data={data}
            providerCampaigns={providerCampaigns}
            startDate={startDate}
            endDate={endDate}
          />
        )}
      </Box>
    </Box>
  );
}
